/**
 * PromoSlider Web Component
 *
 * A web component for horizontally scrolling text with the following features:
 * - Continuous scrolling with infinite loop
 * - Configurable speed and direction
 * - Slows down on hover
 * - Smooth acceleration and deceleration
 */

// Check if the class has already been defined
if (!customElements.get('promo-slider')) {
  customElements.define(
    'promo-slider',
    class PromoSlider extends HTMLElement {
      constructor() {
        super();
        this.isAnimating = false;
        this.animationFrameId = null;
        this.position = 0;
        this.isHovering = false;
        this.cloneCount = 0;
        this.itemSetWidth = 0;
        this.totalWidth = 0;
        this.direction = 'rtl';
        this.speed = 60;
        this.resizeObserver = null;
        this.lastTimestamp = 0;
        this.allItemsLoaded = false;
        this.imagesLoaded = 0;
        this.totalImages = 0;

        // Animation state for smooth transitions
        this.currentSpeed = 0;
        this.targetSpeed = 0;
        this.isTransitioning = false;
        this.transitionStartTime = 0;
        this.transitionDuration = 800; // Increased to 800ms for smoother transitions

        // Bind event handlers for proper cleanup
        this.handleMouseEnter = this.handleMouseEnter.bind(this);
        this.handleMouseLeave = this.handleMouseLeave.bind(this);
        this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
        this.handleResize = this.handleResize.bind(this);
      }

      connectedCallback() {
        // Initialize component when DOM is ready
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.init());
        } else {
          this.init();
        }
      }

      disconnectedCallback() {
        this.cleanup();
      }

      init() {
        // Get references to DOM elements
        this.track = this.querySelector('.promo-slider__track');
        this.wrapper = this.querySelector('.promo-slider__wrapper');

        if (!this.track || !this.wrapper) {
          return;
        }

        // Get configuration from data attributes
        this.direction = this.getAttribute('data-scroll-direction') || 'rtl';
        this.speed = parseInt(this.getAttribute('data-scroll-speed') || '60', 10);

        // Setup styles for continuous scrolling
        this.setupSliderStyles();

        // Wait for images to load before starting animation
        this.waitForImages()
          .then(() => {
            // Clone items for infinite scrolling
            this.cloneItems();

            // Calculate dimensions for animation
            this.calculateDimensions();

            // Set up event listeners
            this.setupEventListeners();

            // Set initial position
            this.resetPosition();

            // Start animation with a short delay to ensure everything is ready
            setTimeout(() => {
              this.startAnimation();
            }, 100);
          })
          .catch((error) => {
            // If there's an error, try to continue anyway
            this.cloneItems();
            this.calculateDimensions();
            this.setupEventListeners();
            this.resetPosition();
            this.startAnimation();
          });
      }

      waitForImages() {
        return new Promise((resolve) => {
          const items = this.querySelectorAll('.promo-slider__item');
          const images = this.querySelectorAll('img');
          const svgs = this.querySelectorAll('svg');

          this.totalImages = images.length + svgs.length;

          // If no images, resolve immediately
          if (this.totalImages === 0) {
            this.allItemsLoaded = true;
            resolve();
            return;
          }

          // Process SVGs immediately since they're inline
          svgs.forEach(() => {
            this.imagesLoaded++;
            if (this.imagesLoaded >= this.totalImages) {
              this.allItemsLoaded = true;
              resolve();
            }
          });

          // Wait for actual images to load
          images.forEach((img) => {
            if (img.complete) {
              this.imagesLoaded++;
            } else {
              img.onload = () => {
                this.imagesLoaded++;
                if (this.imagesLoaded >= this.totalImages) {
                  this.allItemsLoaded = true;
                  resolve();
                }
              };

              img.onerror = () => {
                this.imagesLoaded++;
                if (this.imagesLoaded >= this.totalImages) {
                  this.allItemsLoaded = true;
                  resolve();
                }
              };
            }

            // Safety fallback - if images take too long, proceed anyway
            setTimeout(() => {
              if (!this.allItemsLoaded) {
                this.allItemsLoaded = true;
                resolve();
              }
            }, 2000);
          });

          // If all images are already loaded
          if (this.imagesLoaded >= this.totalImages) {
            this.allItemsLoaded = true;
            resolve();
          }
        });
      }

      setupSliderStyles() {
        // Set container to hide overflow
        this.track.style.overflow = 'hidden';
        this.track.style.width = '100%';

        // Set styles for continuous animation
        this.wrapper.style.display = 'inline-flex';
        this.wrapper.style.boxSizing = 'border-box';
        this.wrapper.style.position = 'relative';
        this.wrapper.style.willChange = 'transform';
        // Prevent flickering
        this.wrapper.style.backfaceVisibility = 'hidden';
        // Disable transitions for smoother animation
        this.wrapper.style.transition = 'none';
      }

      cloneItems() {
        // Get original items
        const items = this.querySelectorAll('.promo-slider__item');
        if (items.length === 0) {
          return;
        }

        // Remove any existing clones first (in case of resize/reinit)
        const clones = this.querySelectorAll('.promo-slider__item.clone');
        clones.forEach((clone) => clone.remove());

        // Calculate how many sets of clones we need based on viewport width
        // We need at least 3 complete sets to ensure seamless looping
        const viewportWidth = window.innerWidth;
        const setsNeeded = Math.max(5, Math.ceil((viewportWidth * 3) / (items[0].offsetWidth * items.length)));
        this.cloneCount = setsNeeded;

        // Process original items for proper display
        items.forEach((item) => {
          // Process any icon SVGs and images for proper display
          const iconSvg = item.querySelector('.promo__icon-svg');
          if (iconSvg) {
            iconSvg.style.display = 'flex';
            iconSvg.style.alignItems = 'center';
            iconSvg.style.justifyContent = 'center';
          }

          // Style all items for consistent display
          item.style.flex = '0 0 auto';
          item.style.padding = '0 40px';
          item.style.minWidth = 'max-content';
        });

        // Clone items multiple times to ensure no blank space
        // First, create full sets in front
        for (let i = 0; i < setsNeeded; i++) {
          items.forEach((item) => {
            const clone = item.cloneNode(true);
            clone.classList.add('clone');
            this.wrapper.appendChild(clone);
          });
        }

        // Then, create full sets at the beginning
        // for (let i = 0; i < setsNeeded; i++) {
        //   items.forEach((item) => {
        //     const clone = item.cloneNode(true);
        //     clone.classList.add('clone');
        //     // Insert at the beginning
        //     this.wrapper.insertBefore(clone, this.wrapper.firstChild);
        //   });
        // }
      }

      calculateDimensions() {
        // Get all items (original + clones)
        const allItems = this.querySelectorAll('.promo-slider__item');

        // Calculate the width of a single set (original items)
        const originalItems = this.querySelectorAll('.promo-slider__item:not(.clone)');

        // Calculate the total width of all items for animation
        this.totalWidth = 0;
        let singleSetWidth = 0;

        // First measure original set width
        originalItems.forEach((item) => {
          const itemWidth = item.offsetWidth;
          singleSetWidth += itemWidth;
        });

        this.itemSetWidth = singleSetWidth;

        // Then get total width
        allItems.forEach((item) => {
          this.totalWidth += item.offsetWidth;
        });

        // Make sure we have enough clones to prevent gaps
        if (this.totalWidth < window.innerWidth * 3) {
          // Add more clones if needed
          const additionalSetsNeeded = Math.ceil((window.innerWidth * 3 - this.totalWidth) / this.itemSetWidth);

          if (additionalSetsNeeded > 0) {
            const originalItemsForCloning = this.querySelectorAll('.promo-slider__item:not(.clone)');

            for (let i = 0; i < additionalSetsNeeded; i++) {
              originalItemsForCloning.forEach((item) => {
                const clone = item.cloneNode(true);
                clone.classList.add('clone');
                this.wrapper.appendChild(clone);
              });
            }

            // Recalculate total width
            this.totalWidth = 0;
            this.querySelectorAll('.promo-slider__item').forEach((item) => {
              this.totalWidth += item.offsetWidth;
            });
          }
        }
      }

      setupEventListeners() {
        // Add hover event listeners to stop animation completely
        this.addEventListener('mouseenter', this.handleMouseEnter);
        this.addEventListener('mouseleave', this.handleMouseLeave);

        // Handle page visibility change
        document.addEventListener('visibilitychange', this.handleVisibilityChange);

        // Observe resize events to recalculate dimensions
        this.resizeObserver = new ResizeObserver(this.handleResize);
        this.resizeObserver.observe(this);
      }

      // Event handler for mouse enter - smoothly stops animation
      handleMouseEnter() {
        this.isHovering = true;
        // Smoothly decelerate animation on hover
        this.smoothStop();
      }

      // Event handler for mouse leave - smoothly resumes animation
      handleMouseLeave() {
        this.isHovering = false;
        // Smoothly accelerate animation on mouse exit
        this.smoothStart();
      }

      handleVisibilityChange() {
        if (document.visibilityState === 'visible') {
          this.lastTimestamp = performance.now();
          this.smoothStart();
        } else {
          this.smoothStop();
        }
      }

      handleResize() {
        // Fully reinitialize on resize to ensure correct dimensions
        this.stopAnimation();
        this.calculateDimensions();
        this.resetPosition();
        this.smoothStart();
      }

      resetPosition() {
        // Position the slider in the middle of the cloned sets
        // This ensures we have items on both sides for seamless looping
        const cloneCount = this.cloneCount;

        // For RTL, start at the first clone set
        // For LTR, start showing the last clone set
        if (this.direction === 'rtl') {
          this.position = -this.itemSetWidth * cloneCount;
        } else {
          this.position = -this.itemSetWidth * cloneCount;
        }

        // Apply the position immediately without animation
        this.wrapper.style.transition = 'none';
        this.wrapper.style.transform = `translate3d(${this.position}px, 0px, 0px)`;

        // Force reflow to ensure the transition is applied (using offsetWidth and not storing it)
        this.wrapper.getBoundingClientRect();
      }

      startAnimation() {
        // Don't start a new animation if one is already running
        if (this.isAnimating) {
          return;
        }

        this.isAnimating = true;
        this.lastTimestamp = performance.now();
        this.targetSpeed = this.speed;
        this.smoothStart();
      }

      smoothStart() {
        if (!this.isAnimating) {
          this.isAnimating = true;
          this.lastTimestamp = performance.now();
        }

        this.targetSpeed = this.speed;
        this.transitionStartTime = performance.now();
        this.isTransitioning = true;

        if (!this.animationFrameId) {
          this.animate(this.lastTimestamp);
        }
      }

      smoothStop() {
        this.targetSpeed = 0;
        this.transitionStartTime = performance.now();
        this.isTransitioning = true;
      }

      stopAnimation() {
        if (this.animationFrameId) {
          cancelAnimationFrame(this.animationFrameId);
          this.animationFrameId = null;
        }
        this.isAnimating = false;
        this.currentSpeed = 0;
        this.targetSpeed = 0;
        this.isTransitioning = false;
      }

      // Easing function for smooth transitions
      easeInOutCubic(t) {
        return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      }

      // Smoother easing function for more natural transitions
      easeInOutQuart(t) {
        return t < 0.5 ? 8 * t * t * t * t : 1 - Math.pow(-2 * t + 2, 4) / 2;
      }

      animate(timestamp) {
        // Calculate time delta for smooth animation regardless of frame rate
        const delta = timestamp - this.lastTimestamp;
        this.lastTimestamp = timestamp;

        // Handle smooth speed transitions
        if (this.isTransitioning) {
          const elapsed = timestamp - this.transitionStartTime;
          const progress = Math.min(elapsed / this.transitionDuration, 1);
          const easedProgress = this.easeInOutQuart(progress);

          // More gradual interpolation for smoother transitions
          const speedDiff = this.targetSpeed - this.currentSpeed;
          this.currentSpeed += speedDiff * 0.08; // Slower interpolation for smoother feel

          // If transition is complete
          if (progress >= 1) {
            this.isTransitioning = false;
            this.currentSpeed = this.targetSpeed;

            // If target speed is 0 and we're not hovering, stop animation
            if (this.targetSpeed === 0 && !this.isHovering) {
              this.stopAnimation();
              return;
            }
          }
        }

        // Use delta to ensure consistent speed regardless of frame rate
        // This prevents stutter on different devices or when the tab loses focus
        // Normalize to 60fps
        const timeRatio = delta / (1000 / 60);

        // Use current speed for animation (which smoothly transitions)
        const pixelsToMove = (this.currentSpeed / 60) * timeRatio;

        // Move position based on direction
        if (this.direction === 'rtl') {
          this.position -= pixelsToMove;

          // If we've moved past the entire set, reset by total content width
          if (this.position <= -this.totalWidth / 2) {
            this.position += this.totalWidth / 2;
          }
        } else {
          this.position += pixelsToMove;

          if (this.position >= 0) {
            this.position -= this.totalWidth / 2;
          }
        }

        // Apply transform with translate3d for smooth GPU-accelerated animation
        this.wrapper.style.transform = `translate3d(${this.position}px, 0px, 0px)`;

        // Continue the animation
        this.animationFrameId = requestAnimationFrame(this.animate.bind(this));
      }

      cleanup() {
        this.stopAnimation();

        // Remove event listeners properly with stored references
        this.removeEventListener('mouseenter', this.handleMouseEnter);
        this.removeEventListener('mouseleave', this.handleMouseLeave);
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);

        // Disconnect resize observer
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
          this.resizeObserver = null;
        }
      }
    },
  );
}
